import { addDays, addMonths } from "date-fns";

import type { BillingModule, BillingPlan } from "./types";
import type {
  BillingCancelCoreModuleDialogViewModel,
  BillingCancelDialogViewModel,
  BillingCancelTrialDialogViewModel,
  BillingEntityActionDialogViewModel,
  BillingPauseDialogViewModel,
  BillingRemoveModuleDialogViewModel,
} from "../types";
import { subscriptionPeriodEndFor } from "./catalog";
import { UNLIMITED_ID } from "./constants";
import { formatModuleList } from "./formatting";
import type { BillingCatalog, BillingSnapshot } from "./types";

type BillingEntity = BillingPlan | BillingModule;

type CancelDialogInput = Readonly<{
  commitmentStartAt?: number;
  entity?: BillingEntity | null;
  kind: "cancel";
  timing: "immediate" | "scheduled";
}>;

type PauseDialogInput = Readonly<{
  commitmentStartAt?: number;
  entity?: BillingEntity | null;
  kind: "pause";
}>;

type RemoveModuleDialogInput = Readonly<{
  catalog: BillingCatalog;
  kind: "remove-module";
  moduleId: string;
  nowSec: number;
  snapshot: BillingSnapshot;
}>;

type CancelCoreModuleDialogInput = Readonly<{
  catalog: BillingCatalog;
  kind: "cancel-core-module";
  moduleId: string;
  nowSec: number;
  snapshot: BillingSnapshot;
  step: "warning" | "confirm";
}>;

type CancelTrialDialogInput = Readonly<{
  catalog: BillingCatalog;
  kind: "cancel-trial";
  moduleId: string;
}>;

export type EntityActionDialogInput =
  | CancelCoreModuleDialogInput
  | CancelDialogInput
  | CancelTrialDialogInput
  | PauseDialogInput
  | RemoveModuleDialogInput;

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const commitmentDates = (
  entity: BillingEntity,
  commitmentStartAt?: number
): { commitmentEnd: Date; effectiveDate: Date } | null => {
  const startAt = commitmentStartAt ?? entity.startDate;
  if (startAt === undefined) {
    return null;
  }

  const commitmentEnd = addMonths(new Date(startAt * 1_000), entity.commitment);
  return { commitmentEnd, effectiveDate: addDays(commitmentEnd, 1) };
};

const projectCancelDialog = ({
  commitmentStartAt,
  entity,
  timing,
}: CancelDialogInput): BillingCancelDialogViewModel | null => {
  if (!entity) {
    return null;
  }
  if (timing === "immediate") {
    return {
      body: [
        "Are you sure you want to cancel your plan? This will take effect at the end of your current billing period.",
      ],
      cancelLabel: "Keep my plan",
      commitmentDate: null,
      confirmLabel: "Yes, cancel",
      effectiveAt: null,
      effectiveDate: null,
      entityId: entity.id,
      entityName: entity.name,
      kind: "cancel",
      title: "Cancel plan",
      timing,
    };
  }

  const dates = commitmentDates(entity, commitmentStartAt);
  if (!dates) {
    return {
      body: [
        `Your current plan has a ${entity.commitment}-month minimum commitment.`,
        "We'll schedule your cancellation for the earliest possible date. You'll keep full access until then and can undo anytime.",
      ],
      cancelLabel: "Keep my plan",
      commitmentDate: null,
      confirmLabel: "Schedule cancellation",
      disabledReason: "Cancellation date unavailable. Please contact support.",
      effectiveAt: null,
      effectiveDate: null,
      entityId: entity.id,
      entityName: entity.name,
      kind: "cancel",
      title: "Are you sure you want to schedule cancellation?",
      timing,
    };
  }

  // Cancellation takes effect at the commitment end itself (sent to Stripe as
  // `cancel_at`), so every surface — body copy, confirm label, and the
  // effectiveAt timestamp — references that date. This is one day earlier than
  // the pause effective date, which begins the day after the commitment fully
  // completes.
  const commitmentDate = formatDate(dates.commitmentEnd);
  return {
    body: [
      `Your current plan has a ${entity.commitment}-month minimum commitment ending ${commitmentDate}.`,
      `We'll schedule your cancellation for the earliest possible date: ${commitmentDate}. You'll keep full access until then and can undo anytime.`,
    ],
    cancelLabel: "Keep my plan",
    commitmentDate,
    confirmLabel: `Schedule cancellation for ${commitmentDate}`,
    effectiveAt: Math.floor(dates.commitmentEnd.getTime() / 1_000),
    effectiveDate: commitmentDate,
    entityId: entity.id,
    entityName: entity.name,
    kind: "cancel",
    title: "Are you sure you want to schedule cancellation?",
    timing,
  };
};

const projectPauseDialog = ({
  commitmentStartAt,
  entity,
}: PauseDialogInput): BillingPauseDialogViewModel | null => {
  if (!entity) {
    return null;
  }

  const dates = commitmentDates(entity, commitmentStartAt);
  if (!dates) {
    return {
      body: [
        `Your current plan has a ${entity.commitment}-month minimum commitment.`,
        "We'll schedule your pause for the earliest possible date. You'll keep full access until then and can undo anytime.",
      ],
      cancelLabel: "Keep my plan",
      commitmentDate: null,
      confirmLabel: "Schedule pause",
      disabledReason: "Pause date unavailable. Please contact support.",
      effectiveAt: null,
      effectiveDate: null,
      entityId: entity.id,
      entityName: entity.name,
      kind: "pause",
      title: "Are you sure you want to schedule a pause?",
    };
  }

  const effectiveDate = formatDate(dates.effectiveDate);
  return {
    body: [
      `Your current plan has a ${entity.commitment}-month minimum commitment ending ${formatDate(dates.commitmentEnd)}.`,
      `We'll schedule your pause for the earliest possible date: ${effectiveDate}. You'll keep full access until then and can undo anytime.`,
    ],
    cancelLabel: "Keep my plan",
    commitmentDate: formatDate(dates.commitmentEnd),
    confirmLabel: `Schedule pause for ${effectiveDate}`,
    effectiveAt: Math.floor(dates.effectiveDate.getTime() / 1_000),
    effectiveDate,
    entityId: entity.id,
    entityName: entity.name,
    kind: "pause",
    title: "Are you sure you want to schedule a pause?",
  };
};

const projectRemoveModuleDialog = ({
  catalog,
  moduleId,
  nowSec,
  snapshot,
}: RemoveModuleDialogInput): BillingRemoveModuleDialogViewModel | null => {
  const module = catalog.activeModules.find(({ id }) => id === moduleId);
  if (!module) {
    return null;
  }

  const remainingModuleIds = catalog.activeModules
    .filter(({ id }) => id !== moduleId)
    .map(({ id }) => id);

  // A removal always executes at the boundary the merchant has already been
  // granted — the end of the cycle they paid for, or the end of a trial they
  // were never billed for. No proration or credit is ever promised.
  const boundary =
    module.trialUnconfirmable && module.trialEndAt !== undefined
      ? module.trialEndAt
      : subscriptionPeriodEndFor(snapshot, catalog, moduleId, nowSec);

  return {
    body: [
      module.trialUnconfirmable
        ? `You'll keep free access until your trial ends on ${formatDate(new Date(boundary * 1_000))}, and you won't be charged for it.`
        : `You'll keep full access through the end of your current billing cycle on ${formatDate(new Date(boundary * 1_000))}.`,
      `After that, ${module.name} will be removed from your plan. You can undo anytime before then.`,
    ],
    cancelLabel: "Keep my plan",
    confirmLabel: `Cancel ${module.name}`,
    currentPlanId:
      catalog.currentPlan?.id === UNLIMITED_ID ? UNLIMITED_ID : null,
    entityId: module.id,
    entityName: module.name,
    kind: "remove-module",
    remainingModuleIds,
    title: `Are you sure you want to cancel ${module.name}?`,
  };
};

const projectCancelTrialDialog = ({
  catalog,
  moduleId,
}: CancelTrialDialogInput): BillingCancelTrialDialogViewModel | null => {
  const module = catalog.activeModules.find(({ id }) => id === moduleId);
  if (!module) {
    return null;
  }

  return {
    // The default outcome leads, so ending the trial now is weighed against
    // what happens if the merchant does nothing.
    body: [
      ...(module.trialEndAt === undefined
        ? []
        : [
            module.trialConverts
              ? `This trial converts to paid on ${formatDate(new Date(module.trialEndAt * 1_000))}, and billing for ${module.name} starts then.`
              : `This trial ends on its own on ${formatDate(new Date(module.trialEndAt * 1_000))} at no cost.`,
          ]),
      `If you end it now, you'll lose access to ${module.name} today. This won't affect the rest of your plan.`,
    ],
    cancelLabel: "Keep trial",
    confirmLabel: "End trial now",
    entityId: module.id,
    entityName: module.name,
    kind: "cancel-trial",
    title: `End your ${module.name} trial now?`,
  };
};

const projectCancelCoreModuleDialog = ({
  catalog,
  moduleId,
  nowSec,
  snapshot,
  step,
}: CancelCoreModuleDialogInput): BillingCancelCoreModuleDialogViewModel | null => {
  const module = catalog.activeModules.find(({ id }) => id === moduleId);
  if (!module) {
    return null;
  }

  const moduleNames = catalog.activeModules.map(({ name }) => name);
  // The whole subscription is cancelled at the end of the cycle already paid
  // for.
  const effectiveDate = formatDate(
    new Date(
      subscriptionPeriodEndFor(snapshot, catalog, moduleId, nowSec) * 1_000
    )
  );

  const shared = {
    cancelLabel: "Keep my plan",
    effectiveDate,
    entityId: module.id,
    entityName: module.name,
    kind: "cancel-core-module",
    moduleNames,
  } as const;

  if (step === "warning") {
    return {
      ...shared,
      body: [
        `${module.name} includes core capabilities other modules depend on. You can cancel other active modules independently. Otherwise, continuing here will schedule your entire plan for cancellation.`,
      ],
      confirmLabel: "Cancel entire plan",
      step,
      title: `Canceling ${module.name} cancels your whole plan`,
    };
  }

  return {
    ...shared,
    body: [
      `You'll keep full access through the end of your current billing cycle on ${effectiveDate}.`,
      `After that, ${formatModuleList(moduleNames)} will be removed from your account. You can undo anytime before then.`,
    ],
    confirmLabel: "Confirm cancel entire plan",
    step,
    title: "Are you sure you want to cancel your entire plan?",
  };
};

export const projectEntityActionDialog = (
  input: EntityActionDialogInput
): BillingEntityActionDialogViewModel | null => {
  switch (input.kind) {
    case "cancel":
      return projectCancelDialog(input);
    case "cancel-core-module":
      return projectCancelCoreModuleDialog(input);
    case "pause":
      return projectPauseDialog(input);
    case "remove-module":
      return projectRemoveModuleDialog(input);
    case "cancel-trial":
      return projectCancelTrialDialog(input);
  }
};
